import * as React from "react";
import { debounce } from "lodash";

const { Component } = React;

import * as UserStore from "../users/UserStore";
import { Modal, Button, Input, FormGroup, Label } from "../components/Modal";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface OrganizationMembershipState {
    searchQuery: string;
    users: Array<any>;
    showAddModal: boolean;
    showRemoveModal: boolean;
    userToAdd: any;
    userToRemove: any;
    membershipType: string;
    source: string;
    sourceID: string;
}

interface OrganizationMembershipProps {
    organization: string;
}

let contextSubscriptionId = "";

export default class OrganizationMembership extends Component<OrganizationMembershipProps, OrganizationMembershipState> {
    constructor(props) {
        super(props);
        this.state = {
            searchQuery: "",
            users: [],
            showAddModal: false,
            showRemoveModal: false,
            userToAdd: undefined,
            userToRemove: undefined,
            membershipType: "0",
            source: "",
            sourceID: "",
        };
    }

    componentDidMount() {
        this.debouncedSearchUsers = debounce(function (query, skip, limit) {
            return UserStore.SearchAllUsers(query, skip, limit).then((response) => {
                this.setState({ users: response.data });
                return response;
            });
        }, 300);

        contextSubscriptionId = GetContext((context) => {
            UserStore.SearchUsers("", 0, 25).then((response) => {
                this.setState({ users: response.data });
                return response;
            });
        });
    }

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    handleQueryChange = (e) => {
        var query = e.target.value;
        this.setState({ searchQuery: query }, () => {
            if (this.state.searchQuery.length > 0) {
                this.debouncedSearchUsers(this.state.searchQuery, 0, 25);
            }
        });
    };

    handleMembershipTypeChange = (e) => {
        var type = e.target.value;
        this.setState({ membershipType: type });
    };

    debouncedSearchUsers = (query, skip, limit) => {
        return UserStore.SearchAllUsers(query, skip, limit).then((response) => {
            this.setState({ users: response.data });
            return response;
        });
    };

    toggleAddModal = () => {
        this.setState((prevState) => ({ showAddModal: !prevState.showAddModal }));
    };

    startAddUserToOrganization = (user) => () => {
        this.setState({
            userToAdd: user,
            showAddModal: true,
            source: "",
            sourceID: "",
        });
    };

    completeAddUserToOrganization = () => {
        try {
            if (this.state.userToAdd.id && this.props.organization && this.state.membershipType) {
                UserStore.AddUserToOrganization(this.state.userToAdd.id, this.props.organization, this.state.membershipType, this.state.source, this.state.sourceID).then((response) => {
                    if (response.status == 200) {
                        this.debouncedSearchUsers(this.state.searchQuery, 0, 25).then(() => {
                            this.setState({
                                showAddModal: false,
                            });
                        });
                    }
                });
            }
        } catch (e) {}
    };

    toggleRemoveModal = () => {
        this.setState((prevState) => ({ showRemoveModal: !prevState.showRemoveModal }));
    };

    startRemoveUserFromOrganization = (user) => () => {
        this.setState({
            userToRemove: user,
            showRemoveModal: true,
        });
    };

    completeRemoveUserFromOrganization = () => {
        try {
            if (this.state.userToRemove.id && this.props.organization && this.state.membershipType) {
                UserStore.RemoveUserFromOrganization(this.state.userToRemove.id, this.props.organization, this.state.membershipType).then((response) => {
                    if (response.status == 200) {
                        this.debouncedSearchUsers(this.state.searchQuery, 0, 25).then(() => {
                            this.setState({
                                showRemoveModal: false,
                            });
                        });
                    }
                });
            }
        } catch (e) {}
    };

    render() {
        return (
            <div className="space-y-6">
                {/* Header Section */}
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <FontAwesomeIcon icon={["fad", "users"]} className="text-3xl text-blue-500" />
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-800">Organization Membership</h2>
                        <p className="text-sm text-gray-600">Manage user memberships in this organization</p>
                    </div>
                </div>

                {/* Search Section */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="relative max-w-2xl">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={["fad", "search"]} className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search users by name or username..."
                            value={this.state.searchQuery}
                            onChange={this.handleQueryChange}
                        />
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {this.state.users.length === 0 ? (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={["fad", "users"]} className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-1">No users found</h3>
                            <p className="text-sm text-gray-500">Try adjusting your search query</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {this.state.users.map((user) => {
                                        var userInOrganization = user.organizationMembership.filter((o: any) => o.organizationID == this.props.organization).length > 0;
                                        var memberships = user.organizationMembership.filter((o: any) => o.organizationID == this.props.organization);

                                        return (
                                            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100">
                                                            <span className="text-blue-600 font-semibold text-sm">{user.fullName?.charAt(0) || user.userName?.charAt(0) || "?"}</span>
                                                        </div>
                                                        <div className="ml-4 min-w-0 flex-1">
                                                            <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                                                            <div className="text-xs text-gray-500 font-mono truncate">{user.id}</div>
                                                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                                                {user.email && (
                                                                    <span className="inline-flex items-center">
                                                                        <FontAwesomeIcon icon={["fad", "envelope"]} className="w-3 h-3 mr-1.5 text-gray-400" />
                                                                        <span className="truncate">{user.email}</span>
                                                                    </span>
                                                                )}
                                                                {user.phoneNumber && (
                                                                    <span className="inline-flex items-center">
                                                                        <FontAwesomeIcon icon={["fad", "phone"]} className="w-3 h-3 mr-1.5 text-gray-400" />
                                                                        {user.phoneNumber}
                                                                    </span>
                                                                )}
                                                                {user.sourceID && (
                                                                    <span className="inline-flex items-center text-xs text-gray-500">
                                                                        <FontAwesomeIcon icon={["fad", "database"]} className="w-3 h-3 mr-1.5 text-gray-400" />
                                                                        {user.sourceID}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {userInOrganization ? (
                                                        <div className="space-y-1">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1" />
                                                                Member
                                                            </span>
                                                            {memberships.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {memberships.map((m: any, idx: number) => (
                                                                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                            {m.membershipType === 0 ? "Staff" : m.membershipType === 1 ? "Student" : m.membershipType === 2 ? "Parent" : "Guest"}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                            <FontAwesomeIcon icon={["fad", "user-slash"]} className="mr-1" />
                                                            Not a member
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end items-center space-x-2">
                                                        {!userInOrganization && (
                                                            <Button variant="primary" size="sm" onClick={this.startAddUserToOrganization(user)}>
                                                                <FontAwesomeIcon icon={["fad", "user-plus"]} className="mr-1" />
                                                                Add to Organization
                                                            </Button>
                                                        )}
                                                        {userInOrganization && (
                                                            <>
                                                                <Button variant="outline-primary" size="sm" onClick={this.startAddUserToOrganization(user)}>
                                                                    <FontAwesomeIcon icon={["fad", "plus"]} className="mr-1" />
                                                                    Add Membership
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={this.startRemoveUserFromOrganization(user)}>
                                                                    <FontAwesomeIcon icon={["fad", "user-minus"]} className="mr-1" />
                                                                    Remove
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <Modal isOpen={this.state.showAddModal} onClose={this.toggleAddModal} title="Add Organization Membership" icon={["fad", "user-plus"]} iconColor="text-green-600">
                    <div className="p-6">
                        {this.state.userToAdd && (
                            <div className="space-y-6">
                                {/* User Info */}
                                <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
                                        <span className="text-blue-600 font-semibold text-lg">{this.state.userToAdd.fullName?.charAt(0) || "?"}</span>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{this.state.userToAdd.fullName}</div>
                                        <div className="text-sm text-gray-600">@{this.state.userToAdd.userName}</div>
                                    </div>
                                </div>

                                {/* Membership Type */}
                                <FormGroup>
                                    <Label htmlFor="membershipType">Membership Type</Label>
                                    <Input id="membershipType" type="select" value={this.state.membershipType} onChange={this.handleMembershipTypeChange}>
                                        <option value="0">👔 Staff</option>
                                        <option value="1">🎓 Student</option>
                                        <option value="2">👨‍👩‍👧 Parent</option>
                                        <option value="3">👤 Guest</option>
                                    </Input>
                                </FormGroup>

                                {/* Source */}
                                <FormGroup>
                                    <Label htmlFor="source">Source (Optional)</Label>
                                    <Input id="source" type="text" placeholder="Enter source identifier" value={this.state.source} onChange={(e) => this.setState({ source: e.target.value })} />
                                    <p className="text-xs text-gray-500 mt-1">Example: https://www.scadco.co.za/?schoolid=</p>
                                </FormGroup>

                                {/* Source ID */}
                                <FormGroup>
                                    <Label htmlFor="sourceID">Source ID (Optional)</Label>
                                    <Input id="sourceID" type="text" placeholder="Enter source ID" value={this.state.sourceID} onChange={(e) => this.setState({ sourceID: e.target.value })} />
                                </FormGroup>
                            </div>
                        )}
                    </div>
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <Button variant="outline-secondary" onClick={this.toggleAddModal}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={this.completeAddUserToOrganization}>
                            <FontAwesomeIcon icon={["fad", "check"]} className="mr-2" />
                            Add Membership
                        </Button>
                    </div>
                </Modal>
                <Modal isOpen={this.state.showRemoveModal} onClose={this.toggleRemoveModal} title="Remove Organization Membership" icon={["fad", "exclamation-triangle"]} iconColor="text-red-600">
                    <div className="p-6">
                        {this.state.userToRemove && (
                            <div className="space-y-4">
                                {/* Warning Alert */}
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start">
                                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-red-600 text-xl mr-3 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-medium text-red-900 mb-1">Confirm Removal</h4>
                                            <p className="text-sm text-red-700">This action will remove all memberships for this user from the organization.</p>
                                        </div>
                                    </div>
                                </div>

                                {/* User Info */}
                                <div className="flex items-center space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                    <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-gray-200">
                                        <span className="text-gray-600 font-semibold text-lg">{this.state.userToRemove.fullName?.charAt(0) || "?"}</span>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{this.state.userToRemove.fullName}</div>
                                        <div className="text-sm text-gray-600">@{this.state.userToRemove.userName}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <Button variant="outline-secondary" onClick={this.toggleRemoveModal}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={this.completeRemoveUserFromOrganization}>
                            <FontAwesomeIcon icon={["fad", "trash"]} className="mr-2" />
                            Remove from Organization
                        </Button>
                    </div>
                </Modal>
            </div>
        );
    }
}
